<script>
  import { onMount } from 'svelte';
  import Card from '../components/Card.svelte';
  import { navigate } from '../router.js';

  export let id = null; // null = list view, string = detail view

  let collections = [];
  let portfolioMap = {}; // slug -> { directory, info }
  let covers = {};       // colId -> imageUrl
  let loading = true;
  let error = '';

  onMount(async () => {
    try {
      const res = await fetch('/portfolio/collections.json');
      if (!res.ok) throw new Error('Failed to load collections');
      collections = await res.json();
      loadCovers(collections);
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  });

  async function loadCovers(cols) {
    await Promise.all(cols.map(async (col) => {
      const firstSlug = col.items[0];
      if (!firstSlug) return;
      try {
        const res = await fetch(`/portfolio/${firstSlug}/info.json`);
        if (!res.ok) return;
        const info = await res.json();
        if (info.images && info.images[0]) {
          covers[col.id] = `/portfolio/${firstSlug}/${info.images[0]}`;
        }
      } catch (e) {}
    }));
    covers = { ...covers };
  }

  async function ensureItems(slugs) {
    const missing = slugs.filter(s => !portfolioMap[s]);
    if (!missing.length) return;
    await Promise.all(missing.map(async (slug) => {
      try {
        const res = await fetch(`/portfolio/${slug}/info.json`);
        if (!res.ok) return;
        const info = await res.json();
        portfolioMap[slug] = { directory: `/portfolio/${slug}`, info };
      } catch (e) {}
    }));
    portfolioMap = { ...portfolioMap };
  }

  $: activeCol = id ? (collections.find(c => c.id === id) || null) : null;
  $: if (activeCol) ensureItems(activeCol.items);
  $: activeItems = activeCol ? activeCol.items.map(s => portfolioMap[s]).filter(Boolean) : [];

  function openCollection(e, colId) {
    e.preventDefault();
    navigate(`/collections/${colId}`);
  }

  function goBack(e) {
    e.preventDefault();
    navigate('/collections');
  }
</script>

<svelte:head>
  {#if activeCol}
    <title>{activeCol.title} — Hanna Tiina Pekk</title>
    <meta name="description" content={activeCol.description || `${activeCol.title} — a collection by Hanna Tiina Pekk.`} />
    <meta property="og:title"       content="{activeCol.title} — Hanna Tiina Pekk" />
    <meta property="og:description" content={activeCol.description || `${activeCol.title} — a collection by Hanna Tiina Pekk.`} />
    <meta name="twitter:title"       content="{activeCol.title} — Hanna Tiina Pekk" />
    <meta name="twitter:description" content={activeCol.description || `${activeCol.title} — a collection by Hanna Tiina Pekk.`} />
  {:else}
    <title>Collections — Hanna Tiina Pekk</title>
    <meta name="description" content="Browse all collections by Hanna Tiina Pekk, fashion designer." />
    <meta property="og:title"       content="Collections — Hanna Tiina Pekk" />
    <meta property="og:description" content="Browse all collections by Hanna Tiina Pekk, fashion designer." />
    <meta name="twitter:title"       content="Collections — Hanna Tiina Pekk" />
    <meta name="twitter:description" content="Browse all collections by Hanna Tiina Pekk, fashion designer." />
  {/if}
</svelte:head>

{#if loading}
  <div class="page-wrap"></div>
{:else if error}
  <div class="page-wrap"><p class="empty">{error}</p></div>
{:else if activeCol}
  <!-- ── Detail view ── -->
  <div class="page-wrap">
    <div class="detail-header">
      <a href="/collections" class="back-link" on:click={goBack}>← Collections</a>
    </div>
    <h1 class="col-title">{activeCol.title}</h1>
    {#if activeCol.year}
      <p class="col-year">{activeCol.year}</p>
    {/if}
    {#if activeCol.description}
      <p class="col-desc">{activeCol.description}</p>
    {/if}
    {#if activeItems.length === 0}
      <p class="empty">No items in this collection yet.</p>
    {:else}
      <div class="card-list">
        {#each activeItems as item}
          <Card directory={item.directory} info={item.info} />
        {/each}
      </div>
    {/if}
  </div>
{:else}
  <!-- ── List view ── -->
  <div class="page-wrap">
    <h1 class="page-title">Collections</h1>
    {#if collections.length === 0}
      <p class="empty">No collections yet.</p>
    {:else}
      <ul class="col-list">
        {#each collections as col}
          <li>
            <a
              href={`/collections/${col.id}`}
              class="col-item"
              on:click={e => openCollection(e, col.id)}
            >
              <div class="col-item-thumb">
                {#if covers[col.id]}
                  <img src={covers[col.id]} alt={col.title} />
                {/if}
              </div>
              <div class="col-item-body">
                <span class="col-item-title">{col.title}</span>
                {#if col.year}<span class="col-item-year">{col.year}</span>{/if}
              </div>
              <span class="col-item-count">{col.items.length} {col.items.length === 1 ? 'item' : 'items'}</span>
            </a>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

<style>
.page-wrap {
  max-width: 960px;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
}

.page-title {
  font-size: 0.8rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #1a1a1a;
  margin: 0 0 2.5rem;
}

/* List */
.col-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid #e0ddd9;
}

.col-item {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 0;
  border-bottom: 1px solid #e0ddd9;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
}

.col-item:hover {
  opacity: 0.7;
}

.col-item-thumb {
  flex-shrink: 0;
  width: 64px;
  height: 80px;
  background: #f0ede9;
  overflow: hidden;
}

.col-item-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.col-item-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}

.col-item-title {
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.col-item-year {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  color: #888;
}

.col-item-count {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  color: #aaa;
  flex-shrink: 0;
}

/* Detail */
.detail-header {
  margin-bottom: 2rem;
}

.back-link {
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #888;
  text-decoration: none;
  transition: color 0.2s;
}

.back-link:hover {
  color: #1a1a1a;
}

.col-title {
  font-size: 0.8rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
}

.col-year {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  color: #888;
  margin: 0 0 1.2rem;
}

.col-desc {
  font-size: 0.78rem;
  line-height: 1.7;
  color: #444;
  max-width: 600px;
  margin: 0 0 2.5rem;
}

.card-list {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: flex-start;
  margin-top: 2rem;
}

.empty {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: #aaa;
}
</style>
